import React, {
	ChangeEvent,
	DragEvent,
	FormEvent,
	KeyboardEvent,
	useRef,
	useState,
} from "react";

interface MessageComposerProps {
	onSendMessage: (data: { text?: string; file?: File }) => void;
}

const MessageComposer: React.FC<MessageComposerProps> = ({ onSendMessage }) => {
	const [message, setMessage] = useState("");
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [dragOver, setDragOver] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const textareaRef = useRef<HTMLTextAreaElement>(null);

	const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
		e?.preventDefault();

		if (!message?.trim() && !selectedFile) return;

		onSendMessage({
			text: message?.trim(),
			file: selectedFile || undefined,
		});

		setMessage("");
		setSelectedFile(null);
		if (textareaRef?.current) {
			textareaRef.current.style.height = "auto";
		}
	};

	const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
		if (e?.key === "Enter" && !e?.shiftKey) {
			e?.preventDefault();
			const form = e.currentTarget.closest("form");
			if (form) {
				form.requestSubmit();
			}
		}
	};

	const handleTextareaChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
		setMessage(e?.target?.value);

		// Auto-resize textarea
		const textarea = e?.target;
		textarea.style.height = "auto";
		textarea.style.height = Math.min(textarea?.scrollHeight, 120) + "px";
	};

	const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
		const file = e?.target?.files?.[0];
		if (file) {
			setSelectedFile(file);
		}
	};

	const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
		e?.preventDefault();
		setDragOver(true);
	};

	const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
		e?.preventDefault();
		setDragOver(false);
	};

	const handleDrop = (e: DragEvent<HTMLDivElement>) => {
		e?.preventDefault();
		setDragOver(false);

		const files = e?.dataTransfer?.files;
		if (files?.length > 0) {
			setSelectedFile(files?.[0]);
		}
	};

	const removeSelectedFile = () => {
		setSelectedFile(null);
		if (fileInputRef?.current) {
			fileInputRef.current.value = "";
		}
	};

	const formatFileSize = (bytes: number) => {
		if (bytes === 0) return "0 Bytes";
		const k = 1024;
		const sizes = ["Bytes", "KB", "MB", "GB"];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + " " + sizes?.[i];
	};

	const quickActions = [
		{ icon: "smile", label: "Emoji", action: () => {} },
		{ icon: "gif", label: "GIF", action: () => {} },
	];

	return (
		<div className="border-t border-border bg-card">
			{/* File Preview */}
			{selectedFile && (
				<div className="p-3 border-b border-border">
					<div className="flex items-center justify-between bg-muted rounded-lg p-3">
						<div className="flex items-center space-x-3">
							<div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
								<svg
									className="w-5 h-5 text-primary"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
									/>
								</svg>
							</div>
							<div>
								<div className="font-medium text-sm text-foreground">
									{selectedFile?.name}
								</div>
								<div className="text-xs text-muted-foreground">
									{formatFileSize(selectedFile?.size)}
								</div>
							</div>
						</div>
						<button
							onClick={removeSelectedFile}
							className="p-1 hover:bg-background rounded-full transition-colors"
						>
							<svg
								className="w-4 h-4 text-muted-foreground"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M6 18L18 6M6 6l12 12"
								/>
							</svg>
						</button>
					</div>
				</div>
			)}
			<form onSubmit={handleSubmit} className="p-4">
				<div
					className={`relative border border-border rounded-xl transition-colors ${
						dragOver ? "border-primary bg-primary/5" : "border-border"
					}`}
					onDragOver={handleDragOver}
					onDragLeave={handleDragLeave}
					onDrop={handleDrop}
				>
					{/* Drag & Drop Overlay */}
					{dragOver && (
						<div className="absolute inset-0 bg-primary/5 border-2 border-dashed border-primary rounded-xl flex items-center justify-center z-10">
							<div className="text-center">
								<svg
									className="w-8 h-8 text-primary mx-auto mb-2"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
									/>
								</svg>
								<p className="text-sm font-medium text-primary">
									Drop files here to attach
								</p>
							</div>
						</div>
					)}

					<div className="flex items-end space-x-3 p-3">
						{/* File Upload Button */}
						<button
							type="button"
							onClick={() => fileInputRef?.current?.click()}
							className="p-2 hover:bg-muted rounded-lg transition-colors shrink-0"
							title="Attach file"
						>
							<svg
								className="w-5 h-5 text-muted-foreground"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
								/>
							</svg>
						</button>

						{/* Text Input */}
						<div className="flex-1">
							<textarea
								ref={textareaRef}
								value={message}
								onChange={handleTextareaChange}
								onKeyPress={handleKeyPress}
								placeholder="Type your message..."
								className="w-full bg-transparent text-foreground placeholder-muted-foreground resize-none focus:outline-none min-h-[40px] max-h-[120px] py-2"
								rows={1}
							/>
						</div>

						{/* Quick Actions */}
						<div className="flex space-x-1 shrink-0">
							{quickActions?.map((action, index) => (
								<button
									key={index}
									type="button"
									onClick={action?.action}
									className="p-2 hover:bg-muted rounded-lg transition-colors"
									title={action?.label}
								>
									{action?.icon === "smile" && (
										<svg
											className="w-5 h-5 text-muted-foreground"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={2}
												d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
											/>
										</svg>
									)}
									{action?.icon === "gif" && (
										<div className="w-5 h-5 text-muted-foreground font-bold text-xs flex items-center justify-center">
											GIF
										</div>
									)}
								</button>
							))}

							{/* Send Button */}
							<button
								type="submit"
								disabled={!message?.trim() && !selectedFile}
								className={`p-2 rounded-lg transition-colors ${
									message?.trim() || selectedFile
										? "bg-primary text-primary-foreground hover:bg-primary/90"
										: "bg-muted text-muted-foreground cursor-not-allowed"
								}`}
								title="Send message"
							>
								<svg
									className="w-5 h-5"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
									/>
								</svg>
							</button>
						</div>
					</div>
				</div>

				{/* Hidden File Input */}
				<input
					ref={fileInputRef}
					type="file"
					onChange={handleFileSelect}
					className="hidden"
					accept="image/*,.pdf,.doc,.docx,.txt"
				/>

				{/* Typing Hint */}
				<div className="mt-2 text-xs text-muted-foreground">
					Press Enter to send, Shift+Enter for new line
				</div>
			</form>
		</div>
	);
};

export default MessageComposer;
